import React, { useContext, useState } from 'react';
import Base from './Base';
import AuthContext from '../context/AuthContext';

/**
 * InvoiceDisplay Component: Displays order details formatted as an invoice, optimized for single-page printing.
 */
function InvoiceDisplay({ invoice }) {
  if (!invoice) {
    return (
      <div className="text-center text-red-600 font-semibold mt-10 print:hidden">
        No invoice data available.
      </div>
    );
  }

  // Destructure invoice data for easier access in JSX
  const {
    seller_info,
    invoice_number,
    invoice_date,
    order_date,
    customer_info,
    shipping_info,
    line_items,
    subtotal,
    discount_amount,
    shipping_charges,
    taxes_total,
    grand_total,
    payment_method,
    payment_status,
    razorpay_order_id,
  } = invoice;

  return (
    // Optimized for A4 printing with compact spacing and single-page layout
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-lg mt-6 mb-6 font-lora
                    print:p-4 print:shadow-none print:bg-white print:border print:border-gray-200
                    print:w-[95%] print:max-w-[210mm] print:mx-auto print:my-2 print:box-border
                    print:text-[0.9em] print:leading-tight">
      <div className="flex justify-between items-start mb-4 border-b pb-2 border-gray-300 print:mb-2 print:pb-1">
        <div>
          <h1 className="text-3xl font-bold text-orange-700 mb-1 print:text-2xl">INVOICE</h1>
          <p className="text-xs text-gray-600 print:text-[10px]">Invoice No: <span className="font-semibold">{invoice_number}</span></p>
          <p className="text-xs text-gray-600 print:text-[10px]">Invoice Date: <span className="font-semibold">{invoice_date}</span></p>
          <p className="text-xs text-gray-600 print:text-[10px]">Order Date: <span className="font-semibold">{order_date}</span></p>
        </div>
        <div className="text-right">
          <h2 className="text-lg font-semibold text-gray-800 print:text-base">{seller_info.name}</h2>
          <p className="text-xs text-gray-600 print:text-[10px]">{seller_info.address_line1}</p>
          <p className="text-xs text-gray-600 print:text-[10px]">{seller_info.address_line2 && `${seller_info.address_line2}, `}{seller_info.city}, {seller_info.state} - {seller_info.pincode}</p>
          <p className="text-xs text-gray-600 print:text-[10px]">{seller_info.country}</p>
          <p className="text-xs text-gray-600 print:text-[10px]">Phone: {seller_info.phone}</p>
          <p className="text-xs text-gray-600 print:text-[10px]">Email: {seller_info.email}</p>
          {seller_info.gstin && <p className="text-xs text-gray-600 print:text-[10px]">GSTIN: {seller_info.gstin}</p>}
        </div>
      </div>

      <div className="flex justify-between mb-4 print:mb-2 print:flex-col print:gap-2">
        <div>
          <h3 className="text-base font-semibold mb-1 text-gray-700 print:text-sm">Bill To:</h3>
          <p className="font-medium text-gray-800 text-sm print:text-xs">{customer_info.name}</p>
          <p className="text-xs text-gray-600 print:text-[10px]">{customer_info.billing_address.address}</p>
          {customer_info.billing_address.address2 && <p className="text-xs text-gray-600 print:text-[10px]">{customer_info.billing_address.address2}</p>}
          <p className="text-xs text-gray-600 print:text-[10px]">{customer_info.billing_address.city}, {customer_info.billing_address.state} - {customer_info.billing_address.pincode}</p>
          <p className="text-xs text-gray-600 print:text-[10px]">{customer_info.billing_address.country}</p>
          <p className="text-xs text-gray-600 print:text-[10px]">Phone: {customer_info.phone}</p>
          <p className="text-xs text-gray-600 print:text-[10px]">Email: {customer_info.email}</p>
        </div>
        <div>
          <h3 className="text-base font-semibold mb-1 text-gray-700 print:text-sm">Ship To:</h3>
          <p className="font-medium text-gray-800 text-sm print:text-xs">{shipping_info.name}</p>
          <p className="text-xs text-gray-600 print:text-[10px]">{shipping_info.address}</p>
          {shipping_info.address2 && <p className="text-xs text-gray-600 print:text-[10px]">{shipping_info.address2}</p>}
          <p className="text-xs text-gray-600 print:text-[10px]">{shipping_info.city}, {shipping_info.state} - {shipping_info.pincode}</p>
          <p className="text-xs text-gray-600 print:text-[10px]">{shipping_info.country}</p>
          <p className="text-xs text-gray-600 print:text-[10px]">Phone: {shipping_info.phone}</p>
        </div>
      </div>

      <div className="mb-4 print:mb-2">
        <h3 className="text-base font-semibold mb-2 text-gray-700 print:text-sm print:mb-1">Order Summary:</h3>
        <table className="w-full text-left border-collapse print:avoid-break-inside">
          <thead>
            <tr className="bg-gray-100 border-b border-gray-300">
              <th className="py-1 px-2 text-xs font-medium text-gray-700 print:text-[10px] print:py-0.5 print:px-1">Item</th>
              <th className="py-1 px-2 text-xs font-medium text-gray-700 print:text-[10px] print:py-0.5 print:px-1">Net Amount</th>
              <th className="py-1 px-2 text-xs font-medium text-gray-700 print:text-[10px] print:py-0.5 print:px-1">Qty</th>
              <th className="py-1 px-2 text-xs font-medium text-gray-700 print:text-[10px] print:py-0.5 print:px-1">CGST</th>
              <th className="py-1 px-2 text-xs font-medium text-gray-700 print:text-[10px] print:py-0.5 print:px-1">CGST Amt</th>
              <th className="py-1 px-2 text-xs font-medium text-gray-700 print:text-[10px] print:py-0.5 print:px-1">SGST</th>
              <th className="py-1 px-2 text-xs font-medium text-gray-700 print:text-[10px] print:py-0.5 print:px-1">SGST Amt</th>
              <th className="py-1 px-2 text-xs font-medium text-gray-700 text-right print:text-[10px] print:py-0.5 print:px-1">Total</th>
            </tr>
          </thead>
          <tbody>
            {line_items.map((item, index) => (
              <tr key={index} className="border-b border-gray-200 print:avoid-break-inside">
                <td className="py-1 px-2 text-xs text-gray-800 print:text-[10px] print:py-0.5 print:px-1 print:max-w-[150px]">
                  {item.name}
                  {item.fabric && <span className="block text-[10px] text-gray-500 print:text-[9px]">({item.fabric}{item.color && `, ${item.color}`})</span>}
                  {item.size && item.size !== 'N/A' && <span className="block text-[10px] text-gray-500 print:text-[9px]">Size: {item.size}</span>}
                </td>
                <td className="py-1 px-2 text-xs text-gray-800 text-right print:text-[10px] print:py-0.5 print:px-1">Rs. {item.net_amount ? item.net_amount.toFixed(2) : '0.00'}</td>
                <td className="py-1 px-2 text-xs text-gray-800 text-center print:text-[10px] print:py-0.5 print:px-1">{item.quantity}</td>
                <td className="py-1 px-2 text-xs text-gray-800 text-right print:text-[10px] print:py-0.5 print:px-1">{item.cgst_percent}%</td>
                <td className="py-1 px-2 text-xs text-gray-800 text-right print:text-[10px] print:py-0.5 print:px-1">Rs. {item.cgst_amount ? item.cgst_amount.toFixed(2) : '0.00'}</td>
                <td className="py-1 px-2 text-xs text-gray-800 text-right print:text-[10px] print:py-0.5 print:px-1">{item.sgst_percent}%</td>
                <td className="py-1 px-2 text-xs text-gray-800 text-right print:text-[10px] print:py-0.5 print:px-1">Rs. {item.sgst_amount ? item.sgst_amount.toFixed(2) : '0.00'}</td>
                <td className="py-1 px-2 text-xs text-gray-800 text-right print:text-[10px] print:py-0.5 print:px-1">Rs. {item.total_price ? item.total_price.toFixed(2) : '0.00'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50">
              <td colSpan="7" className="py-1 px-2 text-xs font-semibold text-gray-700 text-right print:text-[10px] print:py-0.5 print:px-1">Subtotal:</td>
              <td className="py-1 px-2 text-xs font-semibold text-gray-800 text-right print:text-[10px] print:py-0.5 print:px-1">Rs. {subtotal ? subtotal.toFixed(2) : '0.00'}</td>
            </tr>
            {discount_amount > 0 && (
              <tr className="bg-gray-50">
                <td colSpan="7" className="py-1 px-2 text-xs font-semibold text-gray-700 text-right print:text-[10px] print:py-0.5 print:px-1">Discount:</td>
                <td className="py-1 px-2 text-xs font-semibold text-gray-800 text-right print:text-[10px] print:py-0.5 print:px-1">-Rs. {discount_amount.toFixed(2)}</td>
              </tr>
            )}
            <tr className="bg-gray-50">
              <td colSpan="7" className="py-1 px-2 text-xs font-semibold text-gray-700 text-right print:text-[10px] print:py-0.5 print:px-1">Shipping:</td>
              <td className="py-1 px-2 text-xs font-semibold text-gray-800 text-right print:text-[10px] print:py-0.5 print:px-1">Rs. {shipping_charges.toFixed(2)}</td>
            </tr>
            {taxes_total > 0 && (
              <tr className="bg-gray-50">
                <td colSpan="7" className="py-1 px-2 text-xs font-semibold text-gray-700 text-right print:text-[10px] print:py-0.5 print:px-1">Total Taxes:</td>
                <td className="py-1 px-2 text-xs font-semibold text-gray-800 text-right print:text-[10px] print:py-0.5 print:px-1">Rs. {taxes_total.toFixed(2)}</td>
              </tr>
            )}
            <tr className="bg-orange-100 border-t-2 border-orange-700">
              <td colSpan="7" className="py-2 px-2 text-base font-bold text-orange-700 text-right print:text-sm print:py-1 print:px-1">Grand Total:</td>
              <td className="py-2 px-2 text-base font-bold text-orange-700 text-right print:text-sm print:py-1 print:px-1">Rs. {grand_total ? grand_total.toFixed(2) : '0.00'}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="flex justify-between items-end mt-4 border-t pt-2 border-gray-300 print:mt-2 print:pt-1 print:flex-col print:gap-2">
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-1 print:text-xs print:mb-0.5">Payment Information:</p>
          <p className="text-xs text-gray-600 print:text-[10px]">Method: <span className="font-medium">{payment_method}</span></p>
          <p className="text-xs text-gray-600 print:text-[10px]">Status: <span className="font-medium">{payment_status}</span></p>
          {razorpay_order_id && <p className="text-xs text-gray-600 print:text-[10px]">Razorpay Order ID: <span className="font-medium">{razorpay_order_id}</span></p>}
        </div>
        <div className="text-right print:text-left">
          <p className="text-xs text-gray-600 print:text-[10px]">Thank you for your business!</p>
          <p className="text-[10px] text-gray-500 mt-1 print:text-[9px] print:mt-0.5">This is a computer generated invoice and does not require a signature.</p>
        </div>
      </div>

      <div className="flex gap-4 mt-6 justify-center print:hidden">
        <button
          onClick={() => window.print()}
          className="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 px-6 rounded transition"
        >
          Print Invoice
        </button>
      </div>
    </div>
  );
}

/**
 * AdminDashBoard Component: Main admin panel for fetching and displaying order details.
 */
export default function AdminDashBoard() {
  const { user, authToken } = useContext(AuthContext);
  const [orderIdInput, setOrderIdInput] = useState('');
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!user || user.email !== "rekhithasree@gmail.com") {
    return (
      <Base
        Content={
          <div className="text-center text-red-600 font-semibold mt-10 print:hidden">
            Access Denied: You are not authorized to view this page.
          </div>
        }
      />
    );
  }

  const handleFetchOrder = async (e) => {
    e.preventDefault();
    if (!orderIdInput.trim()) {
      setError('Please enter an Order ID.');
      return;
    }
    setError('');
    setInvoiceData(null);
    setLoading(true);

    try {
      const response = await fetch(
        `https://vasstrinfo.com/api/print_checkout_order/?order_id=${encodeURIComponent(orderIdInput)}`,
        {
          headers: {
            ...(authToken?.access && { Authorization: `Bearer ${authToken.access}` }),
          },
        }
      );

      if (!response.ok) {
        const errorBody = await response.text();
        console.error("Backend Error Response:", response.status, errorBody);

        let errorMessage = `Server error (${response.status})`;
        try {
          const errorJson = JSON.parse(errorBody);
          if (errorJson.error) {
            errorMessage = errorJson.error;
          } else if (errorJson.detail) {
            errorMessage = errorJson.detail;
          } else {
            errorMessage = `Server error (${response.status}): ${errorBody.substring(0, 100)}... (Not valid JSON)`;
          }
        } catch (jsonParseError) {
          errorMessage = `Server error (${response.status}): ${errorBody.substring(0, 100)}... (Not valid JSON)`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (data.invoice_data) {
        setInvoiceData(data.invoice_data);
      } else {
        setError(data.error || 'Invoice data not found in response.');
      }
    } catch (err) {
      console.error("Fetch order error:", err);
      setError(err.message || 'Failed to fetch order. Please check network and server logs.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Base
      Content={
        <>
          <div className="max-w-xl mx-auto mt-10 bg-white p-8 print:hidden">
            <h1 className="text-3xl font-bold text-orange-700 mb-4 text-center">Welcome, Admin!</h1>
            <p className="text-gray-700 mb-8 text-center">This is the admin-only dashboard page.</p>
            <form
              onSubmit={handleFetchOrder}
              className="bg-orange-50 rounded-lg p-6 shadow flex flex-col gap-4"
            >
              <label htmlFor="order_id_input" className="font-medium text-gray-800">
                Enter Order ID:
              </label>
              <input
                type="text"
                name="order_id_input"
                id="order_id_input"
                value={orderIdInput}
                onChange={e => setOrderIdInput(e.target.value)}
                required
                className="border border-orange-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
                placeholder="Order ID"
              />
              <button
                type="submit"
                className="mt-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 px-6 rounded transition"
                disabled={loading}
              >
                {loading ? "Fetching..." : "Fetch Invoice"}
              </button>
            </form>
            {error && (
              <div className="text-center text-red-600 font-semibold mt-4">{error}</div>
            )}
          </div>
          {invoiceData && <InvoiceDisplay invoice={invoiceData} />}
        </>
      }
    />
  );
}